import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Col from 'reactstrap/lib/Col';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import {
  EditorialReviewModel,
  EditorialReviewEntities,
  buildReviewPath,
  buildRatedByPath,
  buildAuthorPath,
} from 'client/data/models/editorial-review';
import { SEOModel } from 'client/data/models/seo';
import { buildWhatsNewPath, VehicleDetailsEntities, VehicleDetailsModel } from 'client/data/models/mmy-details';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { stripHtml } from 'client/utils/string-utils';
import { RATINGS_MAP, TEXT_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { KEY_TO_HASH } from 'site-modules/shared/utils/core-page/editorial-review';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { getRatingScale, getOverallRating } from 'site-modules/shared/utils/editorial-helper';
import { separateFirstParagraph } from 'site-modules/shared/utils/string';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { FirstContentArticles } from 'site-modules/core-page/components/articles/first-content-articles/first-content-articles';
import { CoreScorecard } from 'site-modules/shared/components/editors-review/scorecard/core-scorecard';
import { TruncatedFragment } from 'site-modules/shared/components/truncated-fragment/truncated-fragment';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';
import {
  EditorialHighlightsListsUI,
  HIGHLIGHTS_NAME,
} from 'site-modules/core-page/components/editorial-highlights-lists/editorial-highlights-lists';
import { MakeColumnNewsWidget } from 'site-modules/shared/components/column-news-widget/column-news-widget';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';
import { TrimsComparatorLink } from 'site-modules/shared/components/core-page/trims-comparator-link/trims-comparator-link';

import { ReviewSection } from './editorial-review-section';
import { USED_ITEMS, isUsedDataAvailable } from './utils';

import './editorial-review.scss';

const TRIMS_COMPARATOR_START_YEAR = 2005;

function renderRatingSection(ratings, sectionData) {
  const { title, value, children } = sectionData;
  return (
    ratings[value] && (
      <ReviewSection
        title={ratings[value].title || title}
        id={KEY_TO_HASH[value]}
        body={ratings[value].text}
        rating={ratings[value].rating}
      >
        {children.map(subSection => {
          const { title: subSectionTitle, value: subSectionValue } = subSection;
          return (
            ratings[subSectionValue] && (
              <ReviewSection
                id={`${subSectionValue}-${value}`}
                key={subSectionTitle}
                title={subSectionTitle}
                body={stripHtml(ratings[subSectionValue].text)}
                rating={ratings[subSectionValue].rating}
                titleClasses="size-16 fw-normal text-gray-darker mb-0"
              />
            )
          );
        })}
      </ReviewSection>
    )
  );
}

export function EditorialReviewBodyUI({
  editorialReview = null,
  whatsNewContent = null,
  vehicle = null,
  embeddedModules = null,
  isUsed = false,
  isMobile = true,
  hasRouteYearParam = false,
  headingText = null,
  forceShowScorecard = false,
  showHighlights = false,
  expandReview = false,
  isReordered = false,
  reviewAuthor = null,
  reviewRatedBy = null,
  seoAPIContent = {},
  enableSeot5073 = false,
  disableAiDisclaimerSeot5200 = false,
}) {
  if (!editorialReview || (isUsed && !isUsedDataAvailable(editorialReview))) {
    return null;
  }
  const {
    id: editorialReviewId,
    ratings = {},
    weRecommend,
    summary,
    summarySnippet,
    edmundsSays,
    trimFeaturesBodySnippet,
    body: editorialReviewBody,
    trimFeaturesBody,
    trimTested,
    whatsNew,
    powertrain,
    safety,
    introduction,
    review,
    reviewLink,
    firstContent,
  } = editorialReview;
  const { whatOwnersLike, commonIssues, finalTakeawaysFromEverydayDrivers } = enableSeot5073
    ? get(seoAPIContent, 'firstSentence') || {}
    : {};
  const {
    year,
    model: { name: modelName },
    make: { name: makeName, slug: makeSlug },
  } = vehicle;

  const hasGroupedReview = parseInt(year, 10) > TEXT_REVIEW_FORMAT_LAST_YEAR;
  const articles = get(firstContent, 'articles');
  const media = get(firstContent, 'media');
  const hasRelatedArticles = firstContent && ((articles && articles.length > 0) || (media && media.length > 0));
  const scale = getRatingScale(editorialReview);
  const author = get(reviewAuthor, 'contentMetadata', {});
  const ratedBy = get(reviewRatedBy, 'contentMetadata', {});
  const buildPriceAdModule = get(embeddedModules, 'buildPriceAdModule');

  const hasHighlights =
    showHighlights &&
    (get(editorialReview, 'cons.length') ||
      get(editorialReview, 'pros.length') ||
      get(firstContent, 'bullets.value.length') ||
      get(whatsNewContent, 'length'));

  const editorialHighlights = !!hasHighlights && (
    <EditorialHighlightsListsUI
      editorialReview={editorialReview}
      firstContent={firstContent}
      whatsNewContent={whatsNewContent}
      className="mt-2 mb-1_5 mb-md-2"
      sectionsToRender={[HIGHLIGHTS_NAME.PROS, HIGHLIGHTS_NAME.CONS, HIGHLIGHTS_NAME.WHATS_NEW]}
    />
  );

  if (!reviewLink && !hasRelatedArticles) {
    return (
      !!editorialHighlights && (
        <Fragment>
          <h2 className="heading-3 fw-bold mb-1_5">{headingText || "Edmunds' Expert Review"}</h2>
          {editorialHighlights}
          {buildPriceAdModule}
        </Fragment>
      )
    );
  }

  const [modelsSnippet, modelsBody] = separateFirstParagraph(editorialReviewBody || '');
  const overallTitle = get(ratings, 'overall.title') || "Edmunds' Expert Rating";
  const overallRating = getOverallRating(editorialReview) ? `${getOverallRating(editorialReview)} / ${scale}` : null;

  const hasWhatsNewSection = !hasGroupedReview || !get(whatsNewContent, 'length');
  const summaryTitle = isReordered || !hasGroupedReview ? '' : overallTitle;
  const modelsTrimsBody = trimFeaturesBody || modelsBody;

  const [firstSummary, restSummary] = separateFirstParagraph(summary || '');
  const coreSrpModule = get(embeddedModules, 'coreSrpModule');

  const overallRatingSection = (
    <Fragment>
      {hasWhatsNewSection && (
        <ReviewSection title="What’s new" id={MENU_HASH.WHATS_NEW} titleClasses="heading-4 mb-0_5" body={whatsNew} />
      )}

      {summary ? (
        <Fragment>
          {hasWhatsNewSection ? (
            <Fragment>
              <ReviewSection id={MENU_HASH.MODEL_OVERVIEW} title="Vehicle overview" body={firstSummary} />
              {coreSrpModule}
              <ReviewSection body={restSummary} />
            </Fragment>
          ) : (
            <Fragment>
              <ReviewSection
                id={MENU_HASH.MODEL_OVERVIEW}
                title={summaryTitle}
                body={firstSummary}
                rating={overallRating}
                titleClasses="heading-4 mb-1"
              />
              {coreSrpModule}
              <ReviewSection body={restSummary} />
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <ReviewSection
            title="Edmunds says"
            body={edmundsSays}
            rating={overallRating}
            titleClasses="heading-4 mb-0_5"
          />
          {coreSrpModule}
          <ReviewSection id={MENU_HASH.MODEL_OVERVIEW} title="Vehicle overview" body={introduction || review} />
        </Fragment>
      )}

      {!isReordered && (
        <Fragment>
          <ReviewSection
            title={`${hasRouteYearParam ? `${year} ` : ''}${makeName} ${modelName} models`}
            id={MENU_HASH.TRIM_DESCRIPTIONS}
            body={trimFeaturesBodySnippet || editorialReviewBody}
            titleClasses={classnames({ 'heading-4 mb-1': !isUsed })}
            afterBodyChildren={
              <MakeColumnNewsWidget makeSlug={makeSlug} makeName={makeName} modelName={modelName} className="my-1_5" />
            }
          />
          <ReviewSection body={trimFeaturesBody} id="adjusted-headers" />
        </Fragment>
      )}

      {isReordered && hasGroupedReview && (
        <ScrollElement id={MENU_HASH.RATING_SCORECARD}>
          <ReviewSection
            title={overallTitle}
            rating={overallRating}
            body={summarySnippet}
            titleClasses="heading-4 mb-1"
            allowNoBody
          />
        </ScrollElement>
      )}
      <ReviewSection title="Trim tested" id="trim-tested-section" body={trimTested} />
      {!hasGroupedReview && (
        <Fragment>
          <ReviewSection id="mpg" title="Performance & mpg" body={powertrain} />
          <ReviewSection id="reviewSafety" title="Safety" body={safety} />
          {USED_ITEMS.map(
            ({ title, id, value, dataSet }) =>
              !!(dataSet && editorialReview[dataSet] && editorialReview[dataSet][value]) && (
                <ReviewSection
                  key={value}
                  title={title}
                  id={id}
                  body={editorialReview[dataSet][value].text}
                  rating={editorialReview[dataSet][value].rating}
                />
              )
          )}
        </Fragment>
      )}
    </Fragment>
  );

  // Show FL/FD content if has no editorial review
  if (!reviewLink) {
    return (
      <div className="px-0_5">
        {editorialHighlights}
        <FirstContentArticles articles={articles} isMobile={isMobile} suppressFirstMedia isUsed={isUsed} />
        <hr className="mt-1_5 mb-2" />
        {buildPriceAdModule}
      </div>
    );
  }

  const pricingAd = (
    <div
      className={classnames('editorial-pricing-card mt-2', {
        'is-wired': !isMobile,
        'is-mobile': isMobile,
      })}
    >
      <PricingAd mobile={isMobile} />
      {isMobile && <hr className="mt-1_5 mt-md-1 mb-1" />}
    </div>
  );

  const trimsComparatorLink = parseInt(year, 10) >= TRIMS_COMPARATOR_START_YEAR && (
    <TrimsComparatorLink className="mb-1_5" vehicle={vehicle} isMobile={isMobile} />
  );

  return (
    <div data-tracking-parent="edm-entry-editorial-review">
      <ScrollElement id={`scrollTop-${editorialReviewId}`} />
      {isUsed && isMobile && pricingAd}
      <div className="editorial-review-full-container">
        {hasGroupedReview && !isReordered && (
          <div className="mb-2">
            <ReviewSection
              title={`Which ${modelName} does Edmunds recommend?`}
              id={MENU_HASH.RECOMMENDED_TRIM}
              titleClasses="mb-1_5 heading-3"
              body={weRecommend}
            />
          </div>
        )}
        <h2 className="heading-3 fw-bold mb-1_5">{headingText || "Edmunds' Expert Review"}</h2>
        {author && <ArticleAuthor author={author} ratedBy={ratedBy} lazyLoadImage showBioTooltip />}
        {editorialHighlights}
        <TruncatedFragment
          content={overallRatingSection}
          minHeight={205}
          btnContainerClassName={null}
          btnClassName="mb-1_5"
          expandText="Read more"
          collapseText="Read less"
          ariaLabelHeading="about this model"
          resetParams={{
            offset: SCROLL_OFFSET,
            duration: SCROLL_DURATION,
            to: `scrollTop-${editorialReviewId}`,
          }}
          isTruncated={!expandReview}
        />
        {editorialReview.ratings && (isMobile || forceShowScorecard) && (
          <Col>
            <CoreScorecard ratings={editorialReview.ratings} scale={scale} className="my-1_5" />
          </Col>
        )}
        {hasGroupedReview && (
          <Fragment>
            {renderRatingSection(ratings, RATINGS_MAP.DRIVING)}
            {renderRatingSection(ratings, RATINGS_MAP.COMFORT)}
            {renderRatingSection(ratings, RATINGS_MAP.INTERIOR)}
            {renderRatingSection(ratings, RATINGS_MAP.UTILITY)}
            {renderRatingSection(ratings, RATINGS_MAP.TECHNOLOGY)}
          </Fragment>
        )}
        {isReordered && (
          <Fragment>
            {buildPriceAdModule}
            {hasGroupedReview && (
              <Fragment>
                <ReviewSection
                  title={`Which ${modelName} does Edmunds recommend?`}
                  id={MENU_HASH.RECOMMENDED_TRIM}
                  titleClasses="heading-4 mb-1"
                  body={weRecommend}
                />
                {trimsComparatorLink}
              </Fragment>
            )}
            <ReviewSection
              title={`${hasRouteYearParam ? `${year} ` : ''}${makeName} ${modelName} models`}
              id={MENU_HASH.TRIM_DESCRIPTIONS}
              body={trimFeaturesBodySnippet || modelsSnippet}
              titleClasses={classnames({ 'heading-4 mb-1': !isUsed })}
              afterBodyChildren={
                <Fragment>
                  {!hasGroupedReview && !modelsTrimsBody?.length && trimsComparatorLink}
                  <MakeColumnNewsWidget
                    makeSlug={makeSlug}
                    makeName={makeName}
                    modelName={modelName}
                    className="my-1_5"
                  />
                </Fragment>
              }
            >
              <ReviewSection body={modelsTrimsBody} id="adjusted-headers" />
            </ReviewSection>
            {!hasGroupedReview && !!modelsTrimsBody?.length && trimsComparatorLink}
          </Fragment>
        )}
        {(!!whatOwnersLike || !!commonIssues || !!finalTakeawaysFromEverydayDrivers) && (
          <Fragment>
            <ReviewSection
              id="owners-opinions-section"
              title="Owners' Opinions"
              titleClasses="heading-4 mb-1 mt-1"
              allowNoBody
              afterBodyChildren={
                <Fragment>
                  {!!whatOwnersLike && (
                    <ReviewSection
                      id="what-owners-like-section"
                      title="What Owners Like"
                      body={`<p>${whatOwnersLike}</p>`}
                      TitleTag="h3"
                    />
                  )}
                  {!!commonIssues && (
                    <ReviewSection
                      id="common-issues-section"
                      title="Common Issues"
                      body={`<p>${commonIssues}</p>`}
                      TitleTag="h3"
                    />
                  )}
                  {!!finalTakeawaysFromEverydayDrivers && (
                    <ReviewSection
                      id="final-takeaways-from-everyday-drivers-section"
                      title="Final Takeaways from Everyday Drivers"
                      body={`<p>${finalTakeawaysFromEverydayDrivers}</p>`}
                      TitleTag="h3"
                    />
                  )}
                  {!disableAiDisclaimerSeot5200 && (
                    <div className="d-flex medium">
                      <i className="icon-sparkles size-16" aria-hidden />
                      <em className="text-blue-10 ms-0_25">
                        The Owner Opinions section is AI-generated from the text of owner reviews
                      </em>
                    </div>
                  )}
                </Fragment>
              }
            />
          </Fragment>
        )}
      </div>
    </div>
  );
}

EditorialReviewBodyUI.propTypes = {
  editorialReview: EditorialReviewEntities.EditorialReview,
  reviewAuthor: EditorialReviewEntities.Author,
  reviewRatedBy: EditorialReviewEntities.Author,
  whatsNewContent: VehicleDetailsEntities.WhatsNewContent,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  embeddedModules: PropTypes.shape({
    coreSrpModule: PropTypes.node,
  }),
  isUsed: PropTypes.bool,
  forceShowScorecard: PropTypes.bool,
  isMobile: PropTypes.bool,
  hasRouteYearParam: PropTypes.bool,
  showHighlights: PropTypes.bool,
  headingText: PropTypes.string,
  expandReview: PropTypes.bool,
  isReordered: PropTypes.bool,
  seoAPIContent: PropTypes.shape({
    firstSentence: PropTypes.shape({
      whatOwnersLike: PropTypes.string,
      commonIssues: PropTypes.string,
      finalTakeawaysFromEverydayDrivers: PropTypes.string,
    }),
    metaDescription: PropTypes.string,
    faq: PropTypes.array,
  }),
  enableSeot5073: PropTypes.bool,
  disableAiDisclaimerSeot5200: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  enableSeot5073: get(state, 'featureFlags.enable-seot-5073-used-core-llm-content-gen'),
  disableAiDisclaimerSeot5200: get(state, 'featureFlags.disable-used-core-ai-disclaimer-seot-5200'),
});

export const EditorialReviewBody = connect(mapStateToProps)(
  connectToModel(EditorialReviewBodyUI, {
    reviewAuthor: bindToPath(({ params }) => buildAuthorPath(params), EditorialReviewModel),
    reviewRatedBy: bindToPath(({ params }) => buildRatedByPath(params), EditorialReviewModel),
    editorialReview: bindToPath(({ params }) => buildReviewPath(params), EditorialReviewModel),
    whatsNewContent: bindToPath(
      ({ params, showHighlights }) => showHighlights && buildWhatsNewPath(params),
      VehicleDetailsModel
    ),
    seoAPIContent: bindToPath('content', SEOModel),
  })
);
